import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { 
  ArrowsClockwiseIcon, 
  InfoIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useState } from "react";


export default function Checklist() {
  const [expandedItems, setExpandedItems] = useState<string[]>(["item-0"]);
  
  const healthItems = [
    {
      title: "Track Your Blood Pressure at Home",
      subTasks: [
        "Use a digital BP monitor daily (morning & evening).",
        "Log systolic/diastolic readings and share them with your healthcare provider.",
      ],
      providerAlert: false,
    },
    {
      title: "Take Medications as Prescribed",
      subTasks: [],
      providerAlert: false,
    },
    {
      title: "Limit Salt in Your Diet",
      subTasks: [],
      providerAlert: false,
    },
    {
      title: "Eat a Kidney-Friendly, Heart-Healthy Diet",
      subTasks: [],
      providerAlert: true,
    },
    {
      title: "Stay Physically Active",
      subTasks: [],
      providerAlert: false,
    },
    {
      title: "Monitor for Kidney Symptoms",
      subTasks: [],
      providerAlert: false,
    },
    {
      title: "Get Regular Lab Tests",
      subTasks: [],
      providerAlert: false,
    },
  ];

  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  return (
    <Box p={"sm"}>
      <Card
        p={"md"}
        bg={
          colorScheme === "dark"
            ? theme.colors.neutral[9]
            : theme.colors.neutral[2]
        }
        mb={"md"}
      >
        <Group gap={"xs"}>
          <InfoIcon
            size={25}
            color={
              colorScheme === "dark"
                ? theme.colors.neutral[0]
                : theme.colors.neutral[8]
            }
          />

          <Text
            size="md"
            c={
              colorScheme === "dark"
                ? theme.colors.neutral[0]
                : theme.colors.neutral[8]
            }
          >
            Recent changes in your health records may affect your priorities.
            Reorder now.
          </Text>
        </Group>
      </Card>

      {/* Header Bar */}
      <Group justify="space-between" align="center" mt={"md"} mb="md">
        <Text
          fw={600}
          size="lg"
          c={
            colorScheme === "dark"
              ? theme.colors.neutral[0]
              : theme.colors.neutral[9]
          }
        >
          Health Priority Checklist
        </Text>
        <Button
          variant="outline"
          leftSection={<ArrowsClockwiseIcon size={16} />}
          c={theme.colors.indigo[9]}
          style={{
            borderColor: theme.colors.indigo[9],
          }}
        >
          Reorder
        </Button>
      </Group>

      {/* Accordion Checklist */}
      <Card
        withBorder
        p="md"
        bg={
          colorScheme === "dark"
            ? theme.colors.neutral[9]
            : theme.colors.indigo[0]
        }
        mt="md"
        radius="md"
      >
        <Stack gap="sm" >
          <Accordion 

          
            value={expandedItems}
            onChange={(value) => setExpandedItems(Array.isArray(value) ? value : [value])}
            multiple
            styles={{
              item: {
                border: "none",
                backgroundColor: "transparent",
                marginBottom: "16px",
              },
              control: {
                padding: "5px 16px",
                backgroundColor:
                  colorScheme === "dark"
                    ? theme.colors.neutral[8]
                    : theme.colors.neutral[1],
                borderRadius: theme.radius.md,
                border: `1px solid ${
                  colorScheme === "dark"
                    ? theme.colors.neutral[7]
                    : theme.colors.neutral[3]
                }`,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                // "&:hover": {
                //   backgroundColor: "rgba(255, 0, 0, 0.12)", // light red tint
                //   borderColor: theme.colors.red[6],
                // },
              },
              panel: {
                padding: 0,
                backgroundColor: "transparent",
              },
            }}
          >


{/* control: {
      "&:hover": {
        backgroundColor: "#f5f5f5", // your hover color
        cursor: "pointer",
      },
    }, */}


            {healthItems.map((item, index) => (
              <Accordion.Item key={index} value={`item-${index}`} style={{ border: "none" }}>
                <Accordion.Control>
                  <Group gap="md" style={{ flex: 1 }}>
                    <Box
                      bg={theme.colors.indigo[9]}
                      px="sm"
                      py={4}
                      style={{
                        minWidth: 40,
                        textAlign: "center",
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `2px solid ${theme.colors.indigo[9]}`,
                      }}
                    >
                      <Text
                        c="white"
                        fw={600}
                        size="sm"
                      >
                        #{index + 1}
                      </Text>
                    </Box>
                    <Text
                      size="md"
                      fw={500}
                      c={
                        colorScheme === "dark"
                          ? theme.colors.neutral[0]
                          : theme.colors.neutral[9]
                      }
                      style={{ flex: 1 }}
                    >
                      {item.title}
                    </Text>
                    {item.providerAlert && (
                      <Badge
                        variant="light"
                        color="blue"
                        size="sm"
                        style={{
                          backgroundColor: theme.colors.blue[0],
                          color: theme.colors.indigo[9],
                          marginRight: "8px",
                        }}
                      >
                        Provider Alert
                      </Badge>
                    )}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Box p="md" pt="sm">
                    <Group justify="flex-end" mb="md">
                      <Button
                        leftSection={<PlusIcon size={16} />}
                        size="sm"
                        variant="light"
                        color="indigo"
                        styles={{
                          root: {
                            backgroundColor: colorScheme === "dark" 
                              ? theme.colors.indigo[5] 
                              : theme.colors.indigo[1],
                            border: `1px solid ${colorScheme === "dark" 
                              ? theme.colors.indigo[6] 
                              : theme.colors.indigo[3]}`,
                            color: colorScheme === "dark" 
                              ? theme.colors.neutral[0] 
                              : theme.colors.indigo[9],
                          },
                        }}
                      >
                        Add Note
                      </Button>
                    </Group>
                    {item.subTasks.length > 0 ? (
                      <Stack gap="md">
                        {item.subTasks.map((subTask, subIndex) => (
                          <Checkbox
                            key={subIndex}
                            label={subTask}
                            size="sm"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">
                        No sub-tasks available
                      </Text>
                    )}
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Stack>
      </Card>
    </Box>
  );
}
